import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js'

import { supabase } from '@/lib/supabase-client'
import { sidebarUserMock } from '@/mocks/navigation'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import { fetchAccessStatus } from '@/lib/access-control'
import { BRAND } from '@/config/brand.generated'

export type UserProfile = {
  firstName: string
  lastName: string
  email: string
  isAdmin: boolean
  adminCheckedAt?: number
}

type UserProfileContextValue = {
  profile: UserProfile
  /**
   * Oturum kontrolü tamamlandı mı? (#492 review)
   * Mount anında admin yetkisi ARTIK verilmiyor; gerçek değer yalnız asenkron
   * `onAuthStateChange` dalından geliyor. Bu bayrak olmadan `RequireAdmin` gibi
   * guard'lar "henüz bilinmiyor" ile "admin değil" durumunu ayırt edemez ve
   * korunmaları planlayıcı sırasına bağlı bir tesadüfe kalırdı.
   */
  isResolved: boolean
  updateProfile: (profile: Omit<UserProfile, 'isAdmin'>) => void
}

const STORAGE_KEY = 'sensei:user-profile'
const ADMIN_TRUST_TTL_MS = 24 * 60 * 60 * 1000

const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined)

const mapSupabaseUserToProfile = (user: User | null): Omit<UserProfile, 'isAdmin'> | null => {
  if (!user) return null

  const fullName =
    (user.user_metadata?.full_name as string | undefined) ||
    (user.user_metadata?.name as string | undefined) ||
    ''

  const nameParts = fullName.split(' ').filter(Boolean)
  const [firstName, ...rest] = nameParts

  return {
    firstName: firstName ?? (user.email ? user.email.split('@')[0] : BRAND.appName),
    lastName: rest.join(' '),
    email: user.email ?? sidebarUserMock.email,
  }
}

const createDefaultProfile = (): UserProfile => {
  const nameParts = sidebarUserMock.name.split(' ').filter(Boolean)
  const [firstName, ...rest] = nameParts

  return {
    firstName: firstName ?? BRAND.appName,
    lastName: rest.join(' ') || 'Kullanıcısı',
    email: sidebarUserMock.email,
    isAdmin: false,
  }
}

const normalizeProfile = (profile: Partial<UserProfile> | null | undefined, isAdmin?: boolean): UserProfile => {
  const defaults = createDefaultProfile()

  return {
    firstName: profile?.firstName ?? defaults.firstName,
    lastName: profile?.lastName ?? defaults.lastName,
    email: profile?.email ?? defaults.email,
    isAdmin: isAdmin ?? profile?.isAdmin ?? defaults.isAdmin,
    adminCheckedAt: profile?.adminCheckedAt,
  }
}

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored) as Partial<UserProfile>
          // Mount anında oturum e-postası HENÜZ BİLİNMEZ, dolayısıyla kayıtlı admin
          // bayrağının bu hesaba ait olduğu doğrulanamaz. Bu yüzden ad/e-posta gibi
          // zararsız alanlar geri yüklenir ama admin yetkisi ASLA verilmez (#492 review).
          // Tek doğruluk kaynağı, e-posta eşitliğini ve TTL'i birlikte kontrol eden
          // `onAuthStateChange` dalıdır; RequireAuth o çözülene kadar zaten hiçbir
          // korumalı içeriği render etmiyor.
          return normalizeProfile(parsed, false)
        }
      } catch (error) {
        console.warn('Profil bilgileri okunamadı, varsayılan değerlere dönülüyor.', error)
      }
    }
    return createDefaultProfile()
  })

  // Supabase yapılandırılmamışsa (public/mock ortam) hiçbir auth olayı gelmez;
  // guard'ları süresiz bekletmemek için o durumda baştan çözülmüş sayılır.
  const [isResolved, setIsResolved] = useState<boolean>(() => !supabase)

  const persistProfile = useCallback((nextProfile: Omit<UserProfile, 'isAdmin'> | UserProfile, isAdmin?: boolean) => {
    const normalized = normalizeProfile(nextProfile, isAdmin)
    setProfile(normalized)

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized))
      } catch (error) {
        console.warn('Profil bilgileri kaydedilemedi.', error)
      }
    }
  }, [])

  const updateProfile = useCallback((nextProfile: Omit<UserProfile, 'isAdmin'>) => {
    // Keep the existing isAdmin status when user updates their profile
    setProfile((prev) => normalizeProfile({ ...nextProfile, adminCheckedAt: prev.adminCheckedAt }, prev.isAdmin))
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return

      if (event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue) as Partial<UserProfile>
          // Başka sekmenin yazdığı kayıt admin yetkisi TAŞIMAZ: bu handler'ın
          // hangi hesabın oturumda olduğunu doğrulama imkânı yok ve TTL'i de
          // atlıyordu. Mevcut admin durumu korunur, yükseltilmez (#492 review).
          setProfile((prev) => normalizeProfile(parsed, prev.isAdmin))
        } catch (error) {
          console.warn('Profil bilgileri storage üzerinden güncellenemedi.', error)
        }
      } else {
        setProfile(createDefaultProfile())
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  useEffect(() => {
    if (!supabase) {
      return
    }

    let mounted = true

    const handleAuthStateChange = async (
      event: AuthChangeEvent,
      session: Session | null,
    ) => {
      if (!mounted) return

      // TOKEN_REFRESHED: session gecerli, sadece token yenilendi.
      // Access check + profile fetch gereksiz — deadlock ve gecici hata riski.
      if (event === 'TOKEN_REFRESHED') return

      const userProfile = mapSupabaseUserToProfile(session?.user ?? null)
      if (userProfile && session?.user) {
        // Fetch admin status from allowed_emails table
        const access = await fetchAccessStatus(session.user.email)
        if (!mounted) return

        if (!access.error) {
          persistProfile({ ...userProfile, adminCheckedAt: Date.now() }, access.isAdmin)
        } else if (!access.isTransient) {
          // Kalıcı hata (401/403/404): yetki gerçekten kaldırılmış ya da oturum geçersiz
          // olabilir. Son bilinen admin güvenini UZATMA — güven yalnızca geçici kesintide
          // (5xx / ağ hatası) korunur (#492).
          persistProfile(userProfile, false)
        } else {
          let storedProfile: Partial<UserProfile> | null = null
          try {
            const stored = window.localStorage.getItem(STORAGE_KEY)
            storedProfile = stored ? JSON.parse(stored) as Partial<UserProfile> : null
          } catch (error) {
            console.warn('Admin güven kaydı okunamadı, yetki kapalı tutuluyor.', error)
          }

          const normalizeEmail = (email: string | undefined) => email?.trim().toLowerCase() ?? ''
          const adminTrustAge = typeof storedProfile?.adminCheckedAt === 'number'
            ? Date.now() - storedProfile.adminCheckedAt
            : null
          const canTrustStoredAdmin =
            normalizeEmail(storedProfile?.email) === normalizeEmail(session.user.email) &&
            adminTrustAge !== null &&
            adminTrustAge < ADMIN_TRUST_TTL_MS &&
            storedProfile?.isAdmin === true

          persistProfile(
            {
              ...userProfile,
              adminCheckedAt: canTrustStoredAdmin ? storedProfile?.adminCheckedAt : undefined,
            },
            canTrustStoredAdmin,
          )
        }
        // Set Sentry user context for error tracking
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
          username: `${userProfile.firstName} ${userProfile.lastName}`.trim(),
        })
      } else {
        persistProfile(createDefaultProfile(), false)
        // Clear Sentry user context on logout
        clearSentryUser()
      }

      // Her iki dal da bittiğinde profil artık "çözülmüş"tür. Guard'lar bu andan
      // önce `isAdmin=false` değerini "admin değil" diye YORUMLAMAMALIDIR (#492 review).
      setIsResolved(true)
    }

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      // Supabase, auth callback Promise'lerini kendi başlangıç kilidi altında bekler.
      // Bu akış fetchAccessStatus -> getSession yoluna girdiği için callback async
      // olursa kilit kendisini bekler ve INITIAL_SESSION hiç yayımlanamaz.
      void handleAuthStateChange(event, session).catch((error) => {
        console.warn('Profil auth olayı işlenemedi.', error)
        if (mounted) setIsResolved(true)
      })
    })

    return () => {
      mounted = false
      subscription?.subscription.unsubscribe()
    }
  }, [persistProfile])

  const value = useMemo<UserProfileContextValue>(
    () => ({ profile, isResolved, updateProfile }),
    [profile, isResolved, updateProfile]
  )

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
}

export function useUserProfile() {
  const context = useContext(UserProfileContext)
  if (!context) {
    throw new Error('useUserProfile yalnızca UserProfileProvider içindeki bileşenlerde kullanılabilir.')
  }
  return context
}

/** Sağlayıcı yoksa `null` döner (fırlatmaz): yalnız "yönetici mi?" gibi fail-closed okumalar için. */
export function useOptionalUserProfile() {
  return useContext(UserProfileContext) ?? null
}
